/*
 *   Copyright (c) 2026 vlinder Labs Private Limited
 *   All rights reserved.
 *   This software is proprietary of Vlinder Labs Pvt. Ltd and can not be distributed / used for any purpose outside of Vlinder Labs Pvt. Ltd without explicit written consent from the copyright owner.
 */

import { VApplication } from '../../application';
import { LoggerModule } from '@vlinder-web/logger-module-react';
import { getAgent } from './agent';

const SERVICE_NAME = 'TelemetryService';
const log: any = LoggerModule.getInstance();

export type TelemetryEventType =
  | 'PAGE_VIEW'
  | 'FORM_START'
  | 'VERIFY_SUBMIT'
  | 'VERIFY_SUCCESS'
  | 'VERIFY_FAILURE'
  | 'VERIFY_INVALID'
  | 'OUTBOUND_CLICK';

export interface TelemetryEventPayload {
  eventType: TelemetryEventType;
  url?: string;
  referrer?: string;
  targetUrl?: string;
  linkName?: string;
  gtin?: string;
  batchNo?: string;
  sessionId?: string;
  orgId?: string;
  metadata?: any;
}

export class TelemetryService {
  private static instance: TelemetryService;
  private vapp: VApplication;
  private telemetryRestAPI: string;

  private constructor(props: any = {}) {
    this.vapp = VApplication.getInstance();
    this.telemetryRestAPI = props?.telemetryRestAPI;
  }

  public static getInstance(props: any = {}): TelemetryService {
    if (!TelemetryService.instance) {
      TelemetryService.instance = new TelemetryService(props);
    }
    return TelemetryService.instance;
  }

  private getSessionId(): string {
    try {
      let sessionId = sessionStorage.getItem('trag_visitor_session_id');
      if (!sessionId) {
        sessionId =
          'sess_' +
          Date.now().toString(36) +
          '_' +
          Math.random().toString(36).substr(2, 9);
        sessionStorage.setItem('trag_visitor_session_id', sessionId);
      }
      return sessionId;
    } catch (e) {
      return 'sess_anon_' + Date.now().toString(36);
    }
  }

  public async trackEvent(
    eventType: TelemetryEventType,
    metadata: Partial<TelemetryEventPayload> = {}
  ) {
    try {
      let _args = {
        apiURL: this.telemetryRestAPI,
        payload: {
          eventType,
          url: window.location.href,
          referrer: document.referrer || '',
          sessionId: this.getSessionId(),
          orgId: 'DIAGE',
          ...metadata,
        },
      };
      const agent = getAgent();
      return agent.recordEvent(_args);
    } catch (err: any) {
      log?.debug?.('Error in trackEvent:', err);
    }
  }

  public handleOutboundRedirect(url: string, linkName: string) {
    if (!url || typeof url !== 'string') return;
    this.trackEvent('OUTBOUND_CLICK', {
      targetUrl: url,
      linkName,
    });
    window.open(url, '_blank', 'noopener,noreferrer');
  }
}

export const getTelemetryService = (): TelemetryService => {
  const app = VApplication.getInstance();
  return app?.getSync('services.TelemetryService') as TelemetryService;
};

export const trackEvent = (
  eventType: TelemetryEventType,
  metadata?: Partial<TelemetryEventPayload>
) => {
  return getTelemetryService()?.trackEvent(eventType, metadata);
};

export const handleOutboundRedirect = (url: string, linkName: string) => {
  return getTelemetryService()?.handleOutboundRedirect(url, linkName);
};
